import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { DatePipe } from "@angular/common";
import { Router, NavigationExtras } from "@angular/router";
//import { AllModules, ColDef, GridOptions, RedrawRowsParams, RowNode } from '@ag-grid-enterprise/all-modules';
import { ColDef, GridOptions, RedrawRowsParams, RowNode, GridApi } from "ag-grid-community";
import { Subscription, Observable, of, BehaviorSubject } from "rxjs";
import { tap, catchError, finalize } from "rxjs/operators";
import { dateComparator } from '../../../../common/components/data-table/comparators/data-table.comparators';
import { DocumentManagementService } from "../../../../common/services/document-management.service";
import { SubHeaderService } from "../../../../core/services/sub-header.service";
import { SubHeader } from "../../../../core/models/sub-header.model";
import { GridFormatterService } from "../../../../common/services/grid-formatter.service";
import { FileQueueQuery, FileQueueKeyword, FileQueueEntry, parseFileNameFromHeaders, QueuedMessage } from "../../../../common/models/onBaseIntegrationAndMapping.model";
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../../core/models/dialog.model";
import { ReportArgs } from "../../../../common/models/reports.model";
import { AgGridBooleanFilterComponent, IBooleanFilterParams } from "../../../../common/components/ag-grid/filters/boolean-filter.component";
import { ButtonRendererComponent } from "../../../../common/components/ag-grid/button-renderer.component";
import { LoaderService } from "../../../../common/services/loader.service";

@Component({
    selector: 'app-onbase-queue-status',
    templateUrl: './onbase-queue-status.component.html',
    styleUrls: ['./onbase-queue-status.component.scss'],
    standalone: false
})
export class OnBaseQueueStatusComponent implements OnInit, OnDestroy {

    confirmRequeueAllDialog: Dialog = new Dialog("This will resubmit all currently unqueued, unuploaded documents to the upload queue.  Depending on the number of items to requeue, this could cause a significant delay in new items being uploaded to OnBase.  Do you wish to continue?", [new DialogButton("No", false, () => { return false; }), new DialogButton("Yes", true, () => { this.confirmRequeueAll(); })]);


    data: FileQueueEntry[];
    @ViewChild("downloadLink") public downloadLink: ElementRef;

    dataLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    get disableRegenerate(): boolean {
        return this.dataLoading.value;
    }

    get documentsNeedingRequeue(): FileQueueEntry[] {
        if (this.data == null) return [];
        return this.data.filter(d => d.onBaseDocumentId == null && d.message == null);
    }

    get excelStyles(): any[] {
        if (this.formatter) return this.formatter.excelStyles;
        return [];
    }

    fromDate: Date;

    gridOptions: GridOptions;
	gridApi?: GridApi;

    queuedDialog: Dialog = new Dialog("Document has been submitted to the upload queue.", [new DialogButton("OK", false, () => { return true; })]);

    selectionOptions: any = [
        { id: 'all', text: 'All' },
        { id: 'completed', text: 'Completed' },
        { id: 'pending', text: 'Pending Upload' },
        { id: 'requeue', text: 'Needs Requeued' }
    ];

    queryOption: string = 'all';

    subHeader: SubHeader = new SubHeader({ title: "Document Queue Status" });
    subscription: Subscription = new Subscription();
    toDate: Date;

    constructor(private queryService: DocumentManagementService, private formatter: GridFormatterService, private subheaderService: SubHeaderService, private dialogService: DynamicDialogService,
            private loader: LoaderService, private datePipe: DatePipe, public router: Router) {
    }

    ngOnInit() {
        this.subheaderService.updateSubHeader(this.subHeader);
		this.gridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.createColumnDefinitions(), false, true, true, true);
		this.gridOptions.onGridReady = (params) => { 
            this.gridApi = params.api; 
            this.onGridReady(params);
        };
        this.fromDate = new Date();
        this.fromDate.setHours(0, 0, 0, 0);
        this.toDate = new Date();
        this.toDate.setHours(23, 59, 59, 59);
        this.gridOptions.overlayLoadingTemplate = '<span class="ag-overlay-loading-center">Please wait ...</span>';
        this.gridOptions.suppressColumnVirtualisation = true;
        this.runReport({ fromDate: this.fromDate, toDate: this.toDate } as ReportArgs);
    }

    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
    }

    confirmRequeueAll(): void {
        this.loader.toggleLoader(true);
        let items = this.documentsNeedingRequeue.map(d => d.identifier);
        const queue$ = this.queryService.enqueueAll(items).pipe(
            catchError((error) => {
                console.error(error);
                this.loader.toggleLoader(false);
                this.dialogService.showError();
                return of(false);
            }),
            tap((success: boolean) => {
                this.loader.toggleLoader(false);
                if (success) {
                    let nodes: RowNode[] = [];
                    this.documentsNeedingRequeue.forEach(item => {
                        item.message = { enqueuedTime: new Date(), content: item.identifier } as QueuedMessage;                        
                        let index = this.data.findIndex(d => d.identifier == item.identifier);
                        if (index != -1) {
                            this.data.splice(index, 1, item);
                            if (this.gridApi != null) {
                                this.gridApi.forEachNode(n => {
                                    if (n.data != null) {
                                        let obj = <FileQueueEntry>n.data;
                                        if (obj.identifier == item.identifier) {
                                            n.data = item;
                                            nodes.push(n as RowNode<any>);
                                        }
                                    }
                                })
                            }
                        }                        
                    });
                    if (nodes.length > 0) {
                        if (this.gridApi != null) {
                            this.gridApi.redrawRows(<RedrawRowsParams>{ rowNodes: nodes });
                        }
                    }
                }
            })
        ).subscribe({
            next: (success: boolean) => {

            },
            error: (error) => {
                console.error(error);
            }
        });
        this.subscription.add(queue$);
    }

    createColumnDefinitions(): ColDef[] {
        return [
            {
                minWidth: 125,
                maxWidth: 125,
                cellRendererSelector: (params) => {
                    if (params.data.onBaseDocumentId == null) {
                        if (params.data.message == null) {
                            return {
								component: ButtonRendererComponent,
                                params: {
                                    buttonText: "Requeue",
                                    action: (obj: any) => this.enqueueItem(obj)
                                }
                            }
                        } else {
                            return {

                            };
                        }
                    } else {
                        return {
							component: ButtonRendererComponent,
                            params: {
                                buttonText: "Download",
                                action: (obj) => this.downloadItem(obj.onBaseDocumentId)
                            }
                        }
                    }
                }
            } as ColDef,
            {
                headerName: "Enqueued Time",
                headerTooltip: "Enqueued Time",
                valueGetter: this.formatter.formatDate("message.enqueuedTime", 'medium', "Not in Queue"),
                valueFormatter: this.formatter.formatDate("message.enqueuedTime", 'medium', "Not in Queue"),
                tooltipValueGetter: this.formatter.formatDate("message.enqueuedTime", 'medium', "Not in Queue"),
                comparator: dateComparator,
                filter: "agDateColumnFilter",
                filterParams: this.formatter.defaultDateFilterParams,
                minWidth: 200,
                maxWidth: 225
            } as ColDef,
            {
                headerName: "Upload Time Stamp",
                headerTooltip: "Upload Time Stamp",
                field: "uploadTimeStamp",
                valueFormatter: this.formatter.formatDate("uploadTimeStamp", "MM/dd/yyyy hh:mm a"),
                valueGetter: this.formatter.formatDate("uploadTimeStamp", "MM/dd/yyyy hh:mm a"),
                tooltipValueGetter: this.formatter.formatDate("uploadTimeStamp"),
                comparator: dateComparator,
                filter: "agDateColumnFilter",
                filterParams: this.formatter.defaultDateFilterParams,
                sort: "desc",
                sortingOrder: ["asc", null, "desc"],
                minWidth: 225,
                maxWidth: 250
            } as ColDef,
            {
                headerName: "Application Number",
                headerTooltip: "Application Number",
                field: "applicationNumber",
                valueFormatter: this.formatter.formatPotentiallyBlankField("applicationNumber", "N/A"),
                tooltipValueGetter: this.formatter.formatPotentiallyBlankField("applicationNumber", "N/A"),
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 200,
                maxWidth: 225
            } as ColDef,
            {
                headerName: "Document Group",
                headerTooltip: "Document Group",
                field: "documentGroup", 
                tooltipField: "documentGroup",
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 200,
                maxWidth: 225
            } as ColDef,
            {
                headerName: "Document Type",
                headerTooltip: "Document Type",
                field: "documentType", 
                tooltipField: "documentType",
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 200,
                maxWidth: 225
            } as ColDef,
            {
                headerName: "Document File Name",
                headerTooltip: "Document File Name",
                field: "fileName",
                tooltipField: "fileName",
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 200,
                maxWidth: 225
            } as ColDef,
            
            {
                headerName: "Company #",
                headerTooltip: "Company #",
                field: "organizationId",
                tooltipField: "organizationId",
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                maxWidth: 140,
                minWidth: 140
            } as ColDef,
            {
                headerName: "Company Name",
                headerTooltip: "Company Name",
                field: "organizationName",
                tooltipField: "organizationName",
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 140,
                maxWidth: 150
            } as ColDef,
            {
                headerName: "Contact Name",
                headerTooltip: "Contact Name",
                field: "uploadedByName",
                tooltipField: "uploadedByName",
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 125,
                maxWidth: 150
            } as ColDef,
            {
                headerName: "API #",
                headerTooltip: "API #",
                field: "apiNumber",
                tooltipField: "apiNumber",
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                resizable: true,
                minWidth: 140,
                cellClass: "stringType",
                maxWidth: 150
            } as ColDef,
            {
                headerName: "Well Name",
                headerTooltip: "Well Name",
                valueGetter: this.keywordFormatter("Well Name"),
                filterValueGetter: this.keywordFormatter("Well Name"),
                tooltipValueGetter: this.keywordFormatter("Well Name"),
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                resizable: true,
                minWidth: 125,
                maxWidth: 150,
                cellClass: "stringType"
            } as ColDef,
            {
                headerName: "Well Number",
                headerTooltip: "Well Number",
                valueGetter: this.keywordFormatter("Well Number"),
                filterValueGetter: this.keywordFormatter("Well Number"),
                tooltipValueGetter: this.keywordFormatter("Well Number"),
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                resizable: true,
                minWidth: 140,
                maxWidth: 150,
                cellClass: "stringType"
            } as ColDef,
            
           
        ] as ColDef[];
    }

    downloadItem(item: number): void {
        this.loader.toggleLoader(true);
        const file$ = this.downloadObservable(item)
            .subscribe({
                next: () => { console.log(`Document ${item} downloaded`) },
                error: (error) => {
                    console.error(error);
                    this.dialogService.showError();
                }
        });
        this.subscription.add(file$);
    }

    downloadObservable(id: number): Observable<any> {
        return this.queryService.download(id).pipe(
            tap((data) => {
                this.loader.toggleLoader(false);
                if (data != null) {
                    const fileName = parseFileNameFromHeaders(data.headers.get("content-disposition"));
                    const blob = new Blob([data.body as Blob], { type: data.body?.type ?? "" });
				    const blobUrl = window.URL.createObjectURL(blob);
				    const anchor = <HTMLAnchorElement>this.downloadLink.nativeElement;
				    anchor.href = blobUrl;
				    anchor.download = fileName;
				    anchor.click();
                }
            })
        );
    }

    downloadReport(): void {
        const now = this.datePipe.transform(new Date(), "yyyy-MM-dd").toString();
        const fileName = `DocumentQueue_${now}`;
        this.formatter.exportDataToExcel(fileName, "Queue", this.gridApi, false);
    }

    enqueueItem(item: FileQueueEntry): void {
        this.loader.toggleLoader(true);
        const queue$ = this.queryService.enqueueItem(item.identifier).pipe(
            catchError((error) => {
                console.error(error);
                this.loader.toggleLoader(false);
                this.dialogService.showError();
                return of(false);
            }),
            tap((success: boolean) => {
                this.loader.toggleLoader(false);
                if (success) {
                    item.message = { enqueuedTime: new Date(), content: item.identifier } as QueuedMessage;
                    let nodes: RowNode[] = [];
                    let index = this.data.findIndex(d => d.identifier == item.identifier);                    
                    if (index != -1) {
                        this.data.splice(index, 1, item);
                        if (this.gridApi != null) {
                            this.gridApi.forEachNode(n => {
                            if (n.data != null) {
                                let obj = <FileQueueEntry>n.data;
                                if (obj.identifier == item.identifier) {
                                    n.data = item;
                                    nodes.push(n as RowNode<any>);
                                }
                            }
                        })
                        }
                    }
                    if (nodes.length > 0) {
                        if (this.gridApi != null) {
                            this.gridApi.redrawRows(<RedrawRowsParams>{ rowNodes: nodes });
                        }
                    }
                }
            })
        ).subscribe({
            next: (success: boolean) => {
                        
            },
            error: (error) => {
                console.error(error);
            }
        });
        this.subscription.add(queue$);
    }

    keywordFormatter(keywordName: string): any {
        return (obj: any) => {
            let formatted = "N/A";
            if (obj != null && obj.data != null && obj.data.keywords != null) {
                const keywords = <FileQueueKeyword[]>obj.data.keywords;
                const word = keywords.find(k => k.keywordName.toLocaleLowerCase().includes(keywordName.toLocaleLowerCase()));
                if (word != null) formatted = word.keywordValue;
            }
            return formatted;
        }
    }

    onGridReady(event: any) {
        this.formatter.gridReadySizeToFit(event);
    }

    regenerateInvoicesReceipts(): void {
        let requeueReceipts = this.documentsNeedingRequeue.filter(d => d.documentType.toLocaleLowerCase().includes('receipt') || d.documentType.toLocaleLowerCase().includes('invoice'));
        let extra: NavigationExtras = {
            state: { receipts: requeueReceipts }
        }
        this.router.navigate(["/admin/regenerate-invoices-receipts"], extra);
    }

    requeueAll(): void {
        this.confirmRequeueAllDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.confirmRequeueAllDialog);
    }

    runReport(args: ReportArgs): void {
        let query = new FileQueueQuery();
        query.includePending = this.queryOption == 'all' || this.queryOption == 'pending';
        query.includeNeedingRequeued = this.queryOption == 'all' || this.queryOption == 'requeue';
        query.includeCompleted = this.queryOption == 'all' || this.queryOption == 'completed';
        query.startDate = args.fromDate;
        query.endDate = args.toDate;
        query.endDate.setHours(23, 59, 59, 59);
        if (this.gridApi) this.gridApi.setGridOption("loading", true);
        let obs$ = this.queryService.queryQueue(query).pipe(
            catchError((error) => {
                console.error(error);
                this.dialogService.showError();
                return of([]);
            }),
            tap((data: FileQueueEntry[]) => {
                this.data = data;
            })
        );
        this.dataLoading.next(true);
        let subs$ = obs$
            .pipe(finalize(() => { this.dataLoading.next(false); }))
            .subscribe({
                next: () => {
                    if (this.gridApi) {
                        this.gridApi.setGridOption("rowData", this.data);
					    this.gridApi.setGridOption("loading", false);         
					    this.gridApi.autoSizeAllColumns();
                    }
                },
                error: (error) => { console.error(error); }
        });
        this.subscription.add(subs$);
    }

}
